import React, { useState, useEffect, useMemo, useRef } from "react";
import "./DocumentDetailView.scss";
import ShowBlob from "common/components/custom/show-blob/ShowBlob";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import { MODEL_OPTIONS } from "../../_shared/constants/dropdown-options";
import { ENV_CONFIG } from "common/config/env.config";
import { useDocumentExtraction } from "../hooks/useDocumentExtraction";
import { useDocumentSave } from "../hooks/useDocumentSave";
import {
  UserContext,
  EditableField
} from "../data/models/document-extraction.model";
import { ExtractionDataProcessor } from "../data/utils/extraction-data-processor.util";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import CircularProgress from "@mui/material/CircularProgress";
interface ExtractedField {
  field: string;
  extractedValue: string;
  confidence: number;
  fieldId?: number;
}

interface DocumentDetailViewProps {
  isOpen: boolean;
  onClose: () => void;
  documentData: {
    fileName: string;
    documentType: string;
    extractedFields: ExtractedField[];
    flowId: string | number;
    validation_status?: string;
  } | null;
  currentConfig?: any | null;
  currentUser?: any;
  onRefetch?: () => Promise<void>;
  onSubmit?: (
    currentConfig: any,
    editableFields: EditableField[],
    otherFields: any[],
    reextractedTokenCost: string | null,
    totalTokenCost: string
  ) => Promise<void>;
  activeTab?: string;
}

const DocumentDetailView: React.FC<DocumentDetailViewProps> = ({
  isOpen,
  onClose,
  documentData,
  currentConfig,
  currentUser,
  onRefetch,
  onSubmit,
  activeTab
}) => {
  const [selectedModel, setSelectedModel] = useState<string>("Select Model");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editableFields, setEditableFields] = useState<EditableField[]>([]);
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    content: string;
    position: { top: number; left: number };
  }>({ visible: false, content: "", position: { top: 0, left: 0 } });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);

  // Get validation status from documentData or currentConfig
  const validationStatus =
    documentData?.validation_status || currentConfig?.validation_status || "";
  const isApproved = validationStatus?.toLowerCase() === "approved";
  const isCompleted = activeTab?.toLowerCase() === "completed";

  const [isError, setIsError] = useState(false);

  // Helper function to check if a key starts with "Score_" (case-insensitive)
  const isScoreKey = (key: string): boolean => {
    return key.toLowerCase().startsWith("score_");
  };

  // Helper function to find the score key for a given field name
  const findScoreKey = (item: any, fieldName: string): string | undefined => {
    return Object.keys(item).find(
      (key) => key.toLowerCase() === `score_${fieldName.toLowerCase()}`
    );
  };
  const [errorInfo, setErrorInfo] = useState<{
    message: string;
    updatedValue: string;
  }>({
    message: "",
    updatedValue: ""
  });
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({});
  const [textAreaValue, setTextAreaValue] = useState<{ [key: string]: string }>(
    {}
  );
  const [isViewTableModalOpen, setIsViewTableModalOpen] = useState(false);
  const [jsonDetails, setJsonDetails] = useState<any>(null);
  const [currentFieldName, setCurrentFieldName] = useState<string>("");
  const [editedJsonDetails, setEditedJsonDetails] = useState<any>(null);
  const [otherFields, setOtherFields] = useState<any[]>([]);
  const [reextractedTokenCost, setReextractedTokenCost] = useState<
    string | null
  >(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  // Initialize the extraction hook
  const { extractDocument, isExtracting } = useDocumentExtraction({
    onSuccess: async (result) => {
      // Check if re-extraction is running in background
      if (
        result.message === "Re-Extraction Completed." ||
        result.message?.includes("Re-Extraction")
      ) {
        showNotification(
          "Re-extraction completed successfully. Refreshing data..."
        );
        // Refetch the document data
        if (onRefetch) {
          await onRefetch();
        }
      } else if (result.data && Array.isArray(result.data)) {
        // Handle immediate response (legacy support)
        handleExtractionSuccess(result);
        showNotification("Data re-extracted successfully");
      } else {
        showNotification(
          "Re-extraction completed successfully. Refreshing data..."
        );
        // Refetch the document data
        if (onRefetch) {
          await onRefetch();
        }
      }
    },
    onError: (error) => {
      showNotification("Re-extraction started in background");
    }
  });

  // Initialize the save hook
  const { saveDocument, isSaving } = useDocumentSave({
    onSuccess: () => {
      showNotification("Data updated successfully");
    },
    onError: (error) => {
      showNotification(error, "error");
    }
  });

  const userContext: UserContext = {
    department: "IT",
    company: "admin",
    username:
      (currentUser.userPrincipalName?.includes("@")
        ? currentUser.userPrincipalName.split("@")[0]
        : undefined) ||
      (currentUser.mail?.includes("@")
        ? currentUser.mail.split("@")[0]
        : undefined) ||
      currentUser.displayName ||
      "Unknown User",
    role: "Extractor"
  };

  const toggleCollapsible = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string,
    fieldId: number
  ) => {
    const value = event.target.value;
    setTextAreaValue((prev) => ({
      ...prev,
      [key]: value
    }));
    setEditableFields((prev) =>
      prev.map((field) =>
        field.field_id === fieldId
          ? { ...field, value_text: value, updated_text: value }
          : field
      )
    );
  };

  const handleHighlightClick = (
    value: string,
    canvasElement: React.RefObject<HTMLCanvasElement>
  ) => {};

  // Tooltip handlers
  const showTooltip = (
    content: string,
    event: React.MouseEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    if (!content.trim()) return;

    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }

    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    setTooltipData({
      visible: true,
      content,
      position: {
        top: rect.top - 20,
        left: rect.right + 10
      }
    });

    // Set timeout to hide after 3 seconds
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setTooltipData({
          visible: false,
          content: "",
          position: { top: 0, left: 0 }
        });
      }
    }, 3000);
  };

  const hideTooltip = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    // Add a small delay to allow user to move cursor to tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      if (!isTooltipHovered) {
        setTooltipData({
          visible: false,
          content: "",
          position: { top: 0, left: 0 }
        });
      }
    }, 200);
  };

  const handleTooltipMouseEnter = () => {
    setIsTooltipHovered(true);
    // Clear timeout when hovering over tooltip
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipHovered(false);
    setTooltipData({
      visible: false,
      content: "",
      position: { top: 0, left: 0 }
    });
  };

  // Cleanup tooltip on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Hide tooltip when user hovers away from both input and tooltip
  useEffect(() => {
    if (tooltipData.visible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          tooltipRef.current &&
          !tooltipRef.current.contains(event.target as Node)
        ) {
          if (!isTooltipHovered) {
            setTooltipData({
              visible: false,
              content: "",
              position: { top: 0, left: 0 }
            });
          }
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [tooltipData.visible, isTooltipHovered]);

  const handleComplexDataEdit = (
    fieldName: string,
    newValue: any,
    fieldId: number
  ) => {
    const stringValue =
      typeof newValue === "object"
        ? JSON.stringify(newValue)
        : String(newValue);

    setEditableFields((prev) =>
      prev.map((field) =>
        field.field_id === fieldId
          ? { ...field, value_text: stringValue, updated_text: stringValue }
          : field
      )
    );

    // Update otherFields to reflect the change
    setOtherFields((prev) =>
      prev.map((item) => {
        const key = Object.keys(item).find((k) => !isScoreKey(k));
        if (key === fieldName) {
          return {
            ...item,
            [fieldName]: newValue
          };
        }
        return item;
      })
    );
  };

  // Helper function to handle successful extraction
  const handleExtractionSuccess = (result: any) => {
    if (!result.data || !Array.isArray(result.data)) return;

    const newFields = ExtractionDataProcessor.extractFields(result.data);

    // Update textAreaValue
    setTextAreaValue(newFields);

    // Convert newFields to array format for editableFields
    // Note: For legacy extraction response, we'll use index as field_id since API doesn't provide it
    const fieldsArray: EditableField[] = Object.entries(newFields).map(
      ([key, value], index) => ({
        field_id: index, // Use index for legacy response
        value_text:
          typeof value === "object" ? JSON.stringify(value) : String(value),
        updated_text: null,
        value_number: null,
        value_date: null,
        confidence_score: null,
        page_number: null,
        bounding_box: null,
        extracted_updated_on: new Date().toISOString(),
        extracted_updated_by: "system"
      })
    );

    setEditableFields(fieldsArray);

    // Set token cost if available
    if (result.tokenCost) {
      setReextractedTokenCost(result.tokenCost);
    }

    // Update otherFields with processed data
    if (result.processedFields) {
      setOtherFields(result.processedFields);
    }
  };

  const handleSaveJsonDetails = () => {
    if (editedJsonDetails && currentFieldName) {
      // Find the field_id for the current field name from filteredFields
      const field = filteredFields.find((f) => f.field === currentFieldName);
      const fieldId = field?.fieldId || 0;
      handleComplexDataEdit(currentFieldName, editedJsonDetails, fieldId);
      setIsViewTableModalOpen(false);
    }
  };

  // Helper function to parse Python-style dict/list strings to JSON
  const parsePythonStyleJSON = (text: string): any => {
    try {
      // First, try direct JSON.parse (for already valid JSON)
      return JSON.parse(text);
    } catch {
      try {
        // Handle Python-style strings with single quotes
        // Replace single quotes with double quotes, but preserve apostrophes in text
        const normalized = text
          // Replace dictionary/list delimiters
          .replace(/{/g, "{")
          .replace(/}/g, "}")
          .replace(/\[/g, "[")
          .replace(/\]/g, "]")
          // Replace Python None with JSON null
          .replace(/:\s*None\s*([,}])/g, ": null$1")
          .replace(/,\s*None\s*([,}])/g, ", null$1")
          .replace(/\[\s*None\s*([,\]])/g, "[null$1")
          // Replace Python True/False with JSON true/false
          .replace(/:\s*True\s*([,}])/g, ": true$1")
          .replace(/:\s*False\s*([,}])/g, ": false$1")
          .replace(/,\s*True\s*([,}])/g, ", true$1")
          .replace(/,\s*False\s*([,}])/g, ", false$1")
          // Replace single quotes around keys and string values
          // This regex finds patterns like 'key': or ': 'value'
          .replace(/'([^']*)':/g, '"$1":') // Replace 'key': with "key":
          .replace(/:\s*'([^']*)'/g, ': "$1"') // Replace : 'value' with : "value"
          .replace(/,\s*'([^']*)':/g, ', "$1":') // Replace , 'key': with , "key":
          .replace(/\[\s*'([^']*)'/g, '["$1"') // Replace ['value' with ["value"
          .replace(/,\s*'([^']*)'/g, ', "$1"') // Replace , 'value' with , "value"
          .replace(/'([^']*)'(?=\s*[,}])/g, '"$1"'); // Replace trailing 'value' with "value"

        return JSON.parse(normalized);
      } catch {
        // If all parsing fails, return original text
        return text;
      }
    }
  };

  const filteredFields = useMemo((): ExtractedField[] => {
    // Priority 1: Check for new API format with extracteddata array
    if (
      currentConfig?.extracteddata &&
      Array.isArray(currentConfig.extracteddata)
    ) {
      const extractedFields = currentConfig.extracteddata
        .filter(
          (field: any) => field.field_name.toLowerCase() !== "total token cost"
        )
        .map((field: any) => {
          // Determine the actual value based on data_type
          let extractedValue: any = "";
          if (
            field.updated_text !== null &&
            field.updated_text !== "" &&
            field.updated_text.trim() !== ""
          ) {
            // Try to parse updated_text if it's a JSON string (array or object)
            const parsed = parsePythonStyleJSON(field.updated_text);
            if (typeof parsed === "object" && parsed !== field.updated_text) {
              extractedValue = parsed; // Successfully parsed to object/array
            } else {
              extractedValue = field.updated_text; // Keep as string
            }
          } else if (field.value_number !== null) {
            extractedValue = String(field.value_number);
          } else if (field.value_date !== null) {
            extractedValue = field.value_date;
          }

          return {
            field: field.field_name,
            extractedValue: extractedValue || "",
            confidence: field.confidence_score || 0,
            fieldId: field.field_id // Include field_id from API
          };
        });

      // Convert to otherFields format with proper handling of complex values
      const converted = extractedFields.map((field: any) => ({
        [field.field]: field.extractedValue,
        [`Score_${field.field}`]: `${field.confidence}%`
      }));
      setOtherFields(converted);

      return extractedFields;
    }
    // Priority 2: Use documentData.extractedFields as fallback
    else {
      const fields =
        documentData?.extractedFields?.filter(
          (field) => field.field.toLowerCase() !== "total token cost"
        ) || [];

      // Convert to otherFields format with JSON parsing for complex values
      const converted = fields.map((field) => {
        let processedValue = field.extractedValue;

        // Try to parse if it's a JSON string
        if (
          typeof field.extractedValue === "string" &&
          field.extractedValue.trim() !== ""
        ) {
          const parsed = parsePythonStyleJSON(field.extractedValue);
          if (typeof parsed === "object" && parsed !== field.extractedValue) {
            processedValue = parsed; // Successfully parsed to object/array
          }
        }

        return {
          [field.field]: processedValue,
          [`Score_${field.field}`]: `${field.confidence}%`
        };
      });
      setOtherFields(converted);

      return fields;
    }
  }, [currentConfig?.extracteddata, documentData?.extractedFields]);

  const totalTokenCost = useMemo((): string => {
    // If we have re-extracted token cost, use that first
    if (reextractedTokenCost !== null) {
      return reextractedTokenCost;
    }

    // Check for new API format with extracteddata array
    if (
      currentConfig?.extracteddata &&
      Array.isArray(currentConfig.extracteddata)
    ) {
      const tokenCostField = currentConfig.extracteddata.find(
        (field: any) => field.field_name.toLowerCase() === "total token cost"
      );
      if (tokenCostField) {
        return (
          tokenCostField.updated_text ||
          tokenCostField.value_number?.toString() ||
          "0"
        );
      }
    }

    // Fallback to documentData
    return (
      documentData?.extractedFields?.find(
        (field) => field.field.toLowerCase() === "total token cost"
      )?.extractedValue || "0"
    );
  }, [
    reextractedTokenCost,
    currentConfig?.extracteddata,
    documentData?.extractedFields
  ]);

  useEffect(() => {
    if (filteredFields.length > 0) {
      const initialFields = filteredFields.reduce(
        (acc: { [key: string]: any }, field: ExtractedField) => {
          // Handle complex values (objects/arrays) by keeping them as-is
          if (
            field.extractedValue !== undefined &&
            field.extractedValue !== null
          ) {
            acc[field.field] = field.extractedValue;
          } else {
            acc[field.field] = "";
          }
          return acc;
        },
        {} as { [key: string]: any }
      );

      // Convert to array format for editableFields
      const fieldsArray: EditableField[] = filteredFields.map((field) => {
        const value = field.extractedValue;
        const valueText =
          typeof value === "object"
            ? JSON.stringify(value)
            : String(value || "");

        return {
          field_id: field.fieldId || 0, // Use actual field_id from API
          value_text: valueText,
          updated_text: valueText,
          value_number: typeof value === "number" ? value : null,
          value_date: null,
          confidence_score: field.confidence / 100,
          page_number: null,
          bounding_box: null,
          extracted_updated_on: new Date().toISOString(),
          extracted_updated_by: "system"
        };
      });

      setEditableFields(fieldsArray);
      setTextAreaValue(initialFields);
    }
  }, [filteredFields]);

  const renderContent = () => {
    if (isError) {
      return (
        <div className="errorModalContent">
          <div className="modal_header">
            An unexpected error occurred while extraction
          </div>
          <div className="modal_body">
            <p>
              Please find more details about the error below and{" "}
              <b>re-extract this document</b> to fix this
            </p>
            <p>Error message: {errorInfo.message}</p>
            <p>Extracted Value: {errorInfo.updatedValue}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="extractionContent">
        {otherFields.map((item, index) => {
          try {
            const [key, value] =
              Object.entries(item).find(([k]) => !isScoreKey(k)) || [];

            if (!key) return null;

            const isJsonObject =
              typeof value === "object" &&
              value !== null &&
              !Array.isArray(value);
            const isArray = Array.isArray(value);

            const scoreKey = findScoreKey(item, key);
            const scoreValue = scoreKey ? item[scoreKey] : "N/A";
            const scorePercent = parseFloat(scoreValue.replace("%", ""));
            const isValidScore = !isNaN(scorePercent);

            if (isJsonObject || isArray) {
              return (
                <div key={index} className="collapsibleSection">
                  <div
                    className="collapsibleHeader"
                    onClick={() => toggleCollapsible(`section-${index}`)}
                  >
                    <div className="collapsibleTitle">
                      <span className="field-number">{index + 1}</span>
                      <span className="statusIndicator"></span>
                      {key}
                    </div>
                    <div className="collapsibleHeaderRight">
                      <div className="confidenceScore">
                        {isValidScore ? (
                          <>
                            <div className="confidenceBar">
                              <div
                                className={`confidenceFill ${
                                  scorePercent >= 80
                                    ? "confidenceHigh"
                                    : scorePercent >= 50
                                    ? "confidenceMedium"
                                    : "confidenceLow"
                                }`}
                                style={{ width: `${scorePercent}%` }}
                              ></div>
                            </div>
                            <span className="confidenceText">
                              {scorePercent}%
                            </span>
                          </>
                        ) : (
                          "N/A"
                        )}
                      </div>
                      <div className="collapsibleIcon">
                        {expandedSections[`section-${index}`] ? "▼" : "▶"}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`collapsibleContent ${
                      expandedSections[`section-${index}`] ? "expanded" : ""
                    }`}
                  >
                    <div className="complexDataContainer">
                      {isArray ? (
                        <div className="arrayData">
                          {value.length > 0 &&
                          typeof value[0] === "object" &&
                          value[0] !== null ? (
                            <div className="tableExtraction">
                              <div className="tablePreview">
                                <table>
                                  <thead>
                                    <tr>
                                      {Object.keys(value[0] || {})
                                        .slice(0, 4)
                                        .map((header, idx) => (
                                          <th key={idx}>{header}</th>
                                        ))}
                                      {Object.keys(value[0] || {}).length >
                                        4 && (
                                        <th>
                                          ... +
                                          {Object.keys(value[0] || {}).length -
                                            4}{" "}
                                          more
                                        </th>
                                      )}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {value.slice(0, 2).map((row, rowIdx) => (
                                      <tr key={rowIdx}>
                                        {Object.values(row || {})
                                          .slice(0, 4)
                                          .map((cell, cellIdx) => (
                                            <td key={cellIdx}>
                                              {typeof cell === "object"
                                                ? JSON.stringify(cell)
                                                : String(cell || "")}
                                            </td>
                                          ))}
                                        {Object.values(row || {}).length >
                                          4 && (
                                          <td className="moreColumns">...</td>
                                        )}
                                      </tr>
                                    ))}
                                    {value.length > 2 && (
                                      <tr>
                                        <td
                                          colSpan={
                                            Object.keys(value[0] || {}).length >
                                            4
                                              ? 5
                                              : 4
                                          }
                                          className="moreRows"
                                        >
                                          ... and {value.length - 2} more rows
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <div className="arrayPreview">
                              <p>Array with {value.length} item(s)</p>
                              {value.length > 0 && (
                                <div className="arraySimplePreview">
                                  {value.slice(0, 2).map((arrayItem, idx) => (
                                    <div key={idx} className="arraySimpleItem">
                                      {typeof arrayItem === "object"
                                        ? JSON.stringify(arrayItem)
                                        : String(arrayItem || "")}
                                    </div>
                                  ))}
                                  {value.length > 2 && (
                                    <div className="arrayMoreItems">
                                      ... and {value.length - 2} more items
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="objectData">
                          <div className="objectProperties">
                            {Object.entries(value).map(
                              ([propKey, propValue], propIdx) => (
                                <div key={propIdx} className="propertyRow">
                                  <div className="propertyKey">{propKey}:</div>
                                  <div className="propertyValue">
                                    {typeof propValue === "object" &&
                                    propValue !== null
                                      ? JSON.stringify(propValue, null, 2)
                                      : String(propValue || "N/A")}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      {
                        <div className="viewDetailsContainer">
                          <button
                            className="viewDetailsBtn"
                            onClick={(e) => {
                              e.preventDefault();
                              setJsonDetails(value);
                              setEditedJsonDetails(
                                JSON.parse(JSON.stringify(value))
                              );
                              setCurrentFieldName(key);
                              setIsViewTableModalOpen(true);
                            }}
                          >
                            View Full Details
                          </button>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              );
            }

            // Render simple fields (string, number, date)
            const stringValue = String(value || "");
            const displayValue =
              textAreaValue[key] !== undefined
                ? textAreaValue[key]
                : stringValue;
            const isLongText = typeof value === "string" && value.length > 25;

            return (
              <div key={index} className="extractionRow">
                <div className="fieldNumber">{index + 1}</div>
                <div className="fieldLabel">{key}</div>
                {isLongText ? (
                  <textarea
                    className="fieldValue"
                    placeholder="Not detected"
                    style={
                      displayValue === ""
                        ? { fontStyle: "italic", color: "#999" }
                        : {
                            color: "#333333",
                            minHeight: "100px",
                            lineHeight: "1.5"
                          }
                    }
                    value={displayValue}
                    onChange={(e) => {
                      const field = filteredFields.find((f) => f.field === key);
                      const fieldId = field?.fieldId || 0;
                      handleTextAreaChange(e, key, fieldId);
                    }}
                    onSelect={(event) =>
                      handleHighlightClick(
                        (event.target as HTMLTextAreaElement).value,
                        canvasRef
                      )
                    }
                    onMouseEnter={(e) => showTooltip(displayValue || "", e)}
                    onMouseLeave={hideTooltip}
                    rows={4}
                    readOnly={isApproved || isExtracting}
                    disabled={isApproved || isExtracting}
                  />
                ) : (
                  <input
                    type="text"
                    className="fieldValue"
                    placeholder="Not detected"
                    style={
                      displayValue === ""
                        ? { fontStyle: "italic", color: "#999" }
                        : { color: "#333333" }
                    }
                    value={displayValue}
                    onChange={(e) => {
                      const field = filteredFields.find((f) => f.field === key);
                      const fieldId = field?.fieldId || 0;
                      handleTextAreaChange(e, key, fieldId);
                    }}
                    onSelect={(event) =>
                      handleHighlightClick(
                        (event.target as HTMLInputElement).value,
                        canvasRef
                      )
                    }
                    onMouseEnter={(e) => showTooltip(displayValue || "", e)}
                    onMouseLeave={hideTooltip}
                    readOnly={isApproved || isExtracting}
                    disabled={isApproved || isExtracting}
                  />
                )}
                <div className="confidenceScore">
                  {isValidScore ? (
                    <>
                      <div className="confidenceBar">
                        <div
                          className={`confidenceFill ${
                            scorePercent >= 80
                              ? "confidenceHigh"
                              : scorePercent >= 50
                              ? "confidenceMedium"
                              : "confidenceLow"
                          }`}
                          style={{ width: `${scorePercent}%` }}
                        ></div>
                      </div>
                      <span className="confidenceText">{scorePercent}%</span>
                    </>
                  ) : (
                    "N/A"
                  )}
                </div>
              </div>
            );
          } catch (error) {
            setErrorInfo({
              message: (error as Error).message,
              updatedValue: JSON.stringify(item)
            });
            setIsError(true);
            return null;
          }
        })}
      </div>
    );
  };

  const handleBlobLoaded = () => {};

  const showNotification = (
    message: string,
    type: "success" | "error" = "success"
  ) => {
    const variant =
      type === "error" ? ToastVariant.ERROR : ToastVariant.SUCCESS;
    ToastMessageUtil.show(message, variant);
  };

  const handleModelChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModel(event.target.value);
  };

  const handleSave = async () => {
    // Use the save hook to perform the save operation
    await saveDocument(
      currentConfig,
      editableFields,
      otherFields,
      userContext,
      reextractedTokenCost,
      totalTokenCost,
      documentData?.flowId || "",
      currentConfig?.id || currentConfig?.document_id || ""
    );
  };

  const handleSubmit = async () => {
    if (onSubmit) {
      await onSubmit(
        currentConfig,
        editableFields,
        otherFields,
        reextractedTokenCost,
        totalTokenCost
      );
    }
  };

  const handleReextract = async () => {
    if (!currentConfig) {
      showNotification("No configuration data available", "error");
      return;
    }

    if (selectedModel === "Select Model") {
      showNotification("Please select a model first", "error");
      return;
    }

    try {
      // Use the custom hook to perform extraction
      const result = await extractDocument(
        currentConfig,
        selectedModel,
        userContext
      );

      // If extraction returns success but no immediate data, it's running in background
      if (result.success && !result.data) {
        showNotification("Re-extraction started in background");
      }
    } catch (error) {
      console.error("Re-extraction error:", error);
      showNotification("Re-extraction failed", "error");
    }
  };

  const handleModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (!isOpen || !documentData) return null;

  return (
    <div className="document-detail-overlay">
      <div
        className="overlay-background"
        onClick={isExtracting ? undefined : onClose}
      >
        <div className="document-detail-modal" onClick={handleModalClick}>
          {/* Header */}
          <div className="modal-header">
            <div className="header-content">
              <div className="breadcrumb">
                {/* <span className="breadcrumb-item">Back</span>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-item">Document Type</span> */}
                <h2 className="breadcrumb-item">Extraction Details</h2>
              </div>
              <div className="header-actions">
                <button
                  className="close-button"
                  onClick={onClose}
                  disabled={isExtracting}
                  style={{
                    opacity: isExtracting ? 0.5 : 1,
                    cursor: isExtracting ? "not-allowed" : "pointer"
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {/* Content with integrated header */}
          <div className="modal-content">
            <div className="content-layout">
              {/* Left side - Document preview */}
              <div className="document-preview">
                <div className="document-header">
                  <div className="document-info">
                    <div className="file-info">
                      <span className="file-label">File name :</span>
                      <span className="file-name">{documentData.fileName}</span>
                    </div>
                  </div>
                </div>

                <div className="document-viewer">
                  <div className="document-placeholder">
                    <ShowBlob
                      blobName={documentData?.fileName}
                      onBlobLoaded={handleBlobLoaded}
                      doc_id={currentConfig?.document_id}
                      flowId={documentData?.flowId}
                    />
                  </div>
                </div>
              </div>

              {/* Right side - Extracted information */}
              <div className="extraction-panel">
                <div className="panel-header">
                  <div className="panel-header-left">
                    <h2>{documentData.documentType}</h2>
                    <p>Configuration profile</p>
                  </div>
                  {isCompleted && validationStatus && (
                    <div
                      className={`validation-status-badge ${
                        validationStatus.toLowerCase() === "approved"
                          ? "status-approved"
                          : validationStatus.toLowerCase() === "rejected"
                          ? "status-rejected"
                          : "status-pending"
                      }`}
                    >
                      <span className="status-label">
                        <span className="status-value">{validationStatus}</span>
                      </span>
                      {validationStatus.toLowerCase() === "rejected" && (
                        <span className="status-note">
                          ***Please re-extract the document if required***
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="extraction-table">
                  <div className="table-header">
                    <div className="header-cell">#</div>
                    <div className="header-cell">Field</div>
                    <div className="header-cell">Extracted value</div>
                    <div className="header-cell">Confidence</div>
                  </div>
                  {renderContent()}
                </div>

                {/* Panel Footer with token cost and action buttons */}
                <div className="panel-footer">
                  <div className="token-cost-section">
                    <span className="token-label">Token cost:</span>
                    <span className="token-value">
                      ${parseFloat(totalTokenCost || "0").toFixed(2)}
                    </span>
                  </div>

                  {!isApproved && (
                    <div className="action-buttonss">
                      <select
                        className="model-select"
                        value={selectedModel}
                        onChange={handleModelChange}
                        disabled={isExtracting}
                      >
                        <option value="Select Model">Select Model</option>
                        {MODEL_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <button
                        className="btn-warning"
                        onClick={handleReextract}
                        disabled={
                          isExtracting || selectedModel === "Select Model"
                        }
                      >
                        {isExtracting ? (
                          <>
                            <CircularProgress
                              size={16}
                              thickness={5}
                              sx={{ color: "white", marginRight: "8px" }}
                            />
                          </>
                        ) : (
                          "Reextract"
                        )}
                      </button>
                      {!isCompleted && (
                        <button
                          className="btn-dark"
                          onClick={handleSave}
                          disabled={isSaving || isSubmitting || isExtracting}
                        >
                          {isSaving ? (
                            <>
                              <CircularProgress
                                size={16}
                                thickness={5}
                                sx={{ color: "white", marginRight: "8px" }}
                              />
                            </>
                          ) : (
                            "Save"
                          )}
                        </button>
                      )}
                      <button
                        className="btn-primary"
                        onClick={handleSubmit}
                        disabled={isSaving || isSubmitting || isExtracting}
                      >
                        {isSubmitting ? (
                          <>
                            <CircularProgress
                              size={16}
                              thickness={5}
                              sx={{ color: "white", marginRight: "8px" }}
                            />
                          </>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Table Modal */}
      {isViewTableModalOpen && (
        <div
          className="view-table-modal-overlay"
          onClick={() => setIsViewTableModalOpen(false)}
        >
          <div
            className="view-table-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="view-table-header">
              <h3>
                {currentFieldName}{" "}
                {Array.isArray(jsonDetails)
                  ? `Details (${jsonDetails.length} items)`
                  : "Details"}
              </h3>
              <div className="modal-actions">
                {!isApproved && (
                  <button
                    className="save-button"
                    onClick={handleSaveJsonDetails}
                    disabled={isExtracting}
                    style={{
                      opacity: isExtracting ? 0.5 : 1,
                      cursor: isExtracting ? "not-allowed" : "pointer"
                    }}
                  >
                    Save Changes
                  </button>
                )}
                <button
                  className="close-button"
                  onClick={() => setIsViewTableModalOpen(false)}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="view-table-content">
              {Array.isArray(editedJsonDetails) ? (
                <div className="array-detail-view">
                  {editedJsonDetails.length > 0 &&
                  typeof editedJsonDetails[0] === "object" ? (
                    // Render as editable table for array of objects
                    <div className="table-view-container">
                      <table className="editable-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            {Object.keys(editedJsonDetails[0] || {}).map(
                              (header, idx) => (
                                <th key={idx}>{header}</th>
                              )
                            )}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editedJsonDetails.map((item, index) => (
                            <tr key={index}>
                              <td className="row-number">{index + 1}</td>
                              {Object.entries(item).map(
                                ([key, value], cellIdx) => {
                                  const stringValue =
                                    value !== undefined && value !== null
                                      ? typeof value === "object"
                                        ? JSON.stringify(value, null, 2)
                                        : String(value)
                                      : "";
                                  const isLongText = stringValue.length > 30;

                                  return (
                                    <td key={cellIdx}>
                                      {typeof value === "object" &&
                                      value !== null ? (
                                        <textarea
                                          className="table-cell-textarea"
                                          value={JSON.stringify(value, null, 2)}
                                          onChange={(e) => {
                                            try {
                                              const parsedValue = JSON.parse(
                                                e.target.value
                                              );
                                              const newEditedDetails = [
                                                ...editedJsonDetails
                                              ];
                                              newEditedDetails[index] = {
                                                ...newEditedDetails[index],
                                                [key]: parsedValue
                                              };
                                              setEditedJsonDetails(
                                                newEditedDetails
                                              );
                                            } catch (error) {
                                              // Invalid JSON, keep updating raw text
                                              const newEditedDetails = [
                                                ...editedJsonDetails
                                              ];
                                              newEditedDetails[index] = {
                                                ...newEditedDetails[index],
                                                [key]: e.target.value
                                              };
                                              setEditedJsonDetails(
                                                newEditedDetails
                                              );
                                            }
                                          }}
                                          onMouseEnter={(e) =>
                                            showTooltip(
                                              JSON.stringify(value, null, 2),
                                              e
                                            )
                                          }
                                          onMouseLeave={hideTooltip}
                                          rows={2}
                                          readOnly={isApproved}
                                          disabled={isApproved}
                                        />
                                      ) : isLongText ? (
                                        <textarea
                                          className="table-cell-textarea"
                                          value={stringValue}
                                          onChange={(e) => {
                                            const newEditedDetails = [
                                              ...editedJsonDetails
                                            ];
                                            newEditedDetails[index] = {
                                              ...newEditedDetails[index],
                                              [key]: e.target.value
                                            };
                                            setEditedJsonDetails(
                                              newEditedDetails
                                            );
                                          }}
                                          onMouseEnter={(e) =>
                                            showTooltip(stringValue, e)
                                          }
                                          onMouseLeave={hideTooltip}
                                          rows={2}
                                          readOnly={isApproved}
                                          disabled={isApproved}
                                        />
                                      ) : (
                                        <input
                                          type="text"
                                          className="table-cell-input"
                                          value={stringValue}
                                          onChange={(e) => {
                                            const newEditedDetails = [
                                              ...editedJsonDetails
                                            ];
                                            newEditedDetails[index] = {
                                              ...newEditedDetails[index],
                                              [key]: e.target.value
                                            };
                                            setEditedJsonDetails(
                                              newEditedDetails
                                            );
                                          }}
                                          onMouseEnter={(e) =>
                                            showTooltip(stringValue, e)
                                          }
                                          onMouseLeave={hideTooltip}
                                          readOnly={isApproved}
                                          disabled={isApproved}
                                        />
                                      )}
                                    </td>
                                  );
                                }
                              )}
                              <td className="action-cell">
                                <button
                                  className="delete-row-btn"
                                  onClick={() => {
                                    if (!isApproved) {
                                      const newEditedDetails =
                                        editedJsonDetails.filter(
                                          (_, i) => i !== index
                                        );
                                      setEditedJsonDetails(newEditedDetails);
                                    }
                                  }}
                                  title={
                                    isApproved
                                      ? "Cannot delete when approved"
                                      : "Delete row"
                                  }
                                  disabled={isApproved}
                                  style={{
                                    opacity: isApproved ? 0.5 : 1,
                                    cursor: isApproved
                                      ? "not-allowed"
                                      : "pointer"
                                  }}
                                >
                                  <DeleteOutlined />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {!isApproved && (
                        <button
                          className="add-row-btn"
                          onClick={() => {
                            // Create a new row with the same structure as existing rows
                            const newRow = Object.keys(
                              editedJsonDetails[0] || {}
                            ).reduce((acc, key) => ({ ...acc, [key]: "" }), {});
                            setEditedJsonDetails([
                              ...editedJsonDetails,
                              newRow
                            ]);
                          }}
                        >
                          + Add Row
                        </button>
                      )}
                    </div>
                  ) : (
                    // Fallback for array of non-objects
                    editedJsonDetails.map((item, index) => (
                      <div key={index} className="array-item">
                        <div className="array-item-header">
                          Item #{index + 1}
                        </div>
                        <div className="array-item-content">
                          <input
                            type="text"
                            className="simple-value-input"
                            value={
                              item !== undefined && item !== null
                                ? String(item)
                                : ""
                            }
                            onChange={(e) => {
                              const newEditedDetails = [...editedJsonDetails];
                              newEditedDetails[index] = e.target.value;
                              setEditedJsonDetails(newEditedDetails);
                            }}
                            onMouseEnter={(e) =>
                              showTooltip(
                                item !== undefined && item !== null
                                  ? String(item)
                                  : "",
                                e
                              )
                            }
                            onMouseLeave={hideTooltip}
                            readOnly={isApproved}
                            disabled={isApproved}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : typeof editedJsonDetails === "object" &&
                editedJsonDetails !== null ? (
                <div className="object-detail-view">
                  <div className="object-properties-detailed">
                    {Object.entries(editedJsonDetails).map(([key, value]) => (
                      <div key={key} className="property-row-detailed">
                        <div className="property-key-detailed">{key}:</div>
                        <div className="property-value-detailed">
                          {typeof value === "object" && value !== null ? (
                            <textarea
                              className="json-textarea"
                              value={JSON.stringify(value, null, 2)}
                              onChange={(e) => {
                                try {
                                  const parsedValue = JSON.parse(
                                    e.target.value
                                  );
                                  setEditedJsonDetails((prev: any) => ({
                                    ...prev,
                                    [key]: parsedValue
                                  }));
                                } catch (error) {
                                  // Invalid JSON, but still update the raw text
                                  setEditedJsonDetails((prev: any) => ({
                                    ...prev,
                                    [key]: e.target.value
                                  }));
                                }
                              }}
                              rows={3}
                              readOnly={isApproved}
                              disabled={isApproved}
                            />
                          ) : (
                            <input
                              type="text"
                              className="property-input"
                              value={
                                value !== undefined && value !== null
                                  ? String(value)
                                  : ""
                              }
                              onChange={(e) => {
                                setEditedJsonDetails((prev: any) => ({
                                  ...prev,
                                  [key]: e.target.value
                                }));
                              }}
                              onMouseEnter={(e) =>
                                showTooltip(
                                  value !== undefined && value !== null
                                    ? String(value)
                                    : "",
                                  e
                                )
                              }
                              onMouseLeave={hideTooltip}
                              readOnly={isApproved}
                              disabled={isApproved}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <textarea
                  className="json-fallback-textarea"
                  value={JSON.stringify(editedJsonDetails, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsedValue = JSON.parse(e.target.value);
                      setEditedJsonDetails(parsedValue);
                    } catch (error) {
                      // Invalid JSON, but still update the raw text
                      setEditedJsonDetails(e.target.value);
                    }
                  }}
                  rows={20}
                  readOnly={isApproved}
                  disabled={isApproved}
                />
              )}
            </div>
          </div>

          {/* Reextraction Overlay */}
          {isExtracting && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                borderRadius: "8px"
              }}
            >
              <div
                style={{
                  backgroundColor: "white",
                  padding: "30px",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "15px"
                }}
              >
                <CircularProgress size={40} />
                <div
                  style={{ fontSize: "16px", color: "#333", fontWeight: 500 }}
                >
                  Re-extracting document...
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  Please wait, this may take a moment
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tooltip */}
      {tooltipData.visible && (
        <div
          ref={tooltipRef}
          className="field-tooltip"
          style={{
            position: "fixed",
            top: `${tooltipData.position.top}px`,
            left: `${tooltipData.position.left}px`
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <div className="tooltip-content">{tooltipData.content}</div>
        </div>
      )}
    </div>
  );
};

export default DocumentDetailView;
